import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Building2, Users } from "lucide-react";

function CompanyModal({ company, onSave, onClose }) {
  const [form, setForm] = useState({ name: company?.name || "", description: company?.description || "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const handleSave = async () => {
    if (!form.name) { setError("Name required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{company ? "Edit" : "Create"} Company</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div><Label className="text-xs text-gray-500">Company Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" /></div>
          <div><Label className="text-xs text-gray-500">Description</Label><Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompanyManagementPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/companies/").catch(() => ({ data: [] }));
    setCompanies(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingCompany) await client.put(`/companies/${editingCompany._id}`, form);
    else await client.post("/companies/", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    await client.delete(`/companies/${id}`);
    load();
  };

  const toggle = async (id, isActive) => {
    await client.put(`/companies/${id}`, { is_active: !isActive });
    load();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">Companies</h1><p className="text-gray-500 text-sm">Manage tenant organizations and their access</p></div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingCompany(null); setShowModal(true); }}><Plus size={16} className="mr-2" /> New Company</Button>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      ) : companies.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Building2 size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No companies yet</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => setShowModal(true)}>Create first company</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {companies.map(c => (
            <div key={c._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#e6f2f5] rounded-md flex items-center justify-center flex-shrink-0"><Building2 size={18} className="text-[#006080]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><p className="font-semibold text-gray-900">{c.name}</p><Badge variant="secondary" className={`text-xs ${c.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{c.is_active ? "Active" : "Inactive"}</Badge></div>
                  {c.description && <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">ID: <span className="font-mono">{c._id}</span></p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => toggle(c._id, c.is_active)}>{c.is_active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingCompany(c); setShowModal(true); }}><Edit2 size={12} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(c._id)}><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <CompanyModal company={editingCompany} onSave={save} onClose={() => { setShowModal(false); setEditingCompany(null); }} />}
    </div>
  );
}
